import React, { useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import styled from "styled-components";
import { FaInstagram, FaTiktok, FaGoodreads, FaAmazon } from "react-icons/fa";
import Newsletter from "../newsletter/Newsletter";
import Footer from "../privacy-policy/Footer"

const Parallax = styled.div`
  background-image: url("/images/parallax.jpg");
  background-attachment: fixed;
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  padding-top: 10px;
`;

const BookContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: clamp(10px, 5vw, 30px);
  margin: clamp(5px, 2vw, 30px);
  padding: clamp(5px, 2vw, 30px);
  border: 5px solid white;

  @media (max-width: 1200px) { 
    flex-direction: column; 

     &.reverse {
      flex-direction: column-reverse;
    }
  }
`;

const CoverImage = styled.img`
  width: 100%;
  height: auto;
  max-width: clamp(320px, 60vw, 900px);

  @media (max-width: 1200px) {
    max-width: clamp(400px, 70vw, 1000px);
  }
`;

const ClipedBox = styled.div`
  background-color: rgba(255, 255, 255, 0.66);
  mask: radial-gradient(40px at 40px 40px, transparent 98%, black) -40px -40px;
  padding: clamp(10px, 5vw, 30px);
`;

const DescriptionContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const Title = styled.h1`
  color: #0360ffa2;
  text-align: center;
  font-family: 'MedievalSharp', serif;
  font-size: clamp(1.5rem, 5vw, 2.8rem);
  padding-bottom: clamp(15px, 3vw, 25px);
`;

const Paragraph = styled.p`
  color: #2b60bdff;
  font-family: 'Mozilla Text', serif;
  font-size: clamp(1rem, 2vw, 1.5rem);
  line-height: 1.6;
`;

const Button = styled.button`
  background-color: #caaeecff;
  color: white;
  font-family: 'Mozilla Text', serif;
  font-size: clamp(1rem, 2vw, 1.5rem);
  font-weight: bold;
  align-self: center;
  margin-top: 15px;
  padding: 10px;
  border: none;
  border-radius: 15px;
  cursor: pointer;
  transition: background-color 0.3s ease;

  &:hover {
    background-color: #ba9debbb;
  }
`;

const FullBox = styled.div`
  background-color: rgba(255, 255, 255, 0.66);
  padding: clamp(15px, 5vw, 60px);

  @media (max-width: 1200px) { 
    margin-top: 15px;
  }
`;

const SpecialText = styled.p`
  font-weight: bold;
  font-style: italic;
  text-align: center;
`;

const ConnectContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 50px;
  margin: clamp(15px, 5vw, 30px);

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 30px;
  }
`;

const LinksContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;

  @media (max-width: 768px) {
    flex-direction: row;
  }
`;

const Link = styled.a`
  color: white;

  svg {
    width: clamp(40px, 5vw, 60px);
    height: auto;
  }

  &:hover {
    color: rgba(255, 255, 255, 0.66);
    transform: scale(1.09);
  }
`;

const Home = () => {
  useEffect(() => {
    document.title = "Home - Anna Miki";
  }, []);

  const navigate = useNavigate();

  return (
    <Parallax>

      <BookContainer>

        <CoverImage src="/images/tropes_b1.svg" alt="Beyond Prejudice by Anna Miki" />
        
        <ClipedBox>

          <DescriptionContainer>

            <Title style={{fontSize: "clamp(1.2rem, 2vw, 2rem)", paddingLeft: "30px", paddingRight: "30px"}}>Beyond Prejudice</Title>

            <Paragraph style={{textAlign: "center"}}>
              She fears the man in her nightmares—but she's falling for his brother. Love and loyalty clash as family secrets unravel, and one choice changes everything.
            </Paragraph>
            
            <Button onClick={() => navigate('/beyond-prejudice')}>
              Learn more
            </Button>
          
          </DescriptionContainer>

        </ClipedBox>

      </BookContainer>

      <BookContainer className="reverse">

        <ClipedBox>

          <DescriptionContainer>

            <Title style={{fontSize: "clamp(1.2rem, 2vw, 2rem)", paddingLeft: "30px", paddingRight: "30px"}}>When The Butterflies Are Gone</Title>
          
            <Paragraph style={{textAlign: "center"}}>
              Spanning continents and generations, this is a story about the legacy of love, the grip of fear, and the choices that define who we become.
            </Paragraph>

            <Button onClick={() => navigate('/when-the-butterflies-are-gone')}>
              Learn more
            </Button>

          </DescriptionContainer>

        </ClipedBox>

        <CoverImage src="/images/tropes_b2.svg" alt="When The Butterflies Are Gone by Anna Miki" />

      </BookContainer>

      <FullBox>

        <Title>About the Author</Title>

        <Paragraph>
          Born and raised in Romania, Anna Miki found her way across the world to the U.S. for a time, and then back home. She writes romance, romantic suspense, and women’s fiction that dives deep into love, longing, and the twists life throws our way. Her stories are rich with high emotions and drama because, let’s face it, the best tales are the ones that make you feel everything.
        </Paragraph> <br/>

        <Paragraph>
          When she’s not dreaming up characters who love fiercely and fight for what they want, you can find her savoring life’s quieter moments—like reading on the beach, where the waves provide the perfect soundtrack to a great book. She’s married to her real-life hero, and together they’ve raised two children who have taught her everything about life and love.
        </Paragraph> <br/>

        <Paragraph>
          <SpecialText>If you’re drawn to stories that stir your soul, she’s your kindred spirit.</SpecialText>
        </Paragraph>

      </FullBox>

      <ConnectContainer>

        <LinksContainer>

          <Link href="https://www.instagram.com/annamiki_author/" target="_blank" rel="noopener noreferrer">
            <FaInstagram />
          </Link>

          <Link href="https://www.tiktok.com/@annamiki84" target="_blank" rel="noopener noreferrer">
            <FaTiktok />
          </Link>

          <Link href="https://www.goodreads.com/author/show/51714605.Anna_Miki" target="_blank" rel="noopener noreferrer">
            <FaGoodreads />
          </Link>

          <Link href="https://www.amazon.com/stores/Anna-Miki/author/B0DKYMFFFN" target="_blank" rel="noopener noreferrer">
            <FaAmazon />
          </Link>

        </LinksContainer>

        <Newsletter />

      </ConnectContainer>

      <Footer />

    </Parallax>
  );
};

export default Home;
